import type { ReactNode } from 'react';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';

export type SortDirection = 'asc' | 'desc';
export type TableSort = { key: string; direction: SortDirection };

/** One column. The first column's cells are the row headers (th scope="row"). */
export type TableColumn<Row> = {
  key: string;
  label: ReactNode;
  /** Numbers: right-aligned, display face, tabular figures. */
  numeric?: boolean;
  align?: 'start' | 'end' | 'center';
  sortable?: boolean;
  render: (row: Row) => ReactNode;
  className?: string;
};

export type TableProps<Row> = {
  columns: TableColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** What the table shows; visually hidden unless `showCaption`. Required for screen readers. */
  caption: ReactNode;
  showCaption?: boolean;
  sort?: TableSort;
  onSortChange?: (sort: TableSort) => void;
  /** Shown in place of rows when there are none. */
  empty?: ReactNode;
  /**
   * Narrow columns: tighter cell padding, headers and cells may wrap, and the table never asks for
   * more than the container (no sideways scroll on a desktop width). Use it for many short columns
   * such as a comparison matrix; pair it with a sticky first column via `className` on that column.
   */
  compact?: boolean;
  className?: string;
};

const ALIGN = { start: 'text-left', end: 'text-right', center: 'text-center' } as const;

/**
 * Rows and columns for comparing many items on the same fields. Scrolls sideways on its own on
 * narrow screens (`compact` fits a desktop width instead and only scrolls on a phone); numbers right-aligned in tabular figures; sortable headers announce aria-sort.
 */
export function Table<Row>({
  columns,
  rows,
  rowKey,
  caption,
  showCaption = false,
  sort,
  onSortChange,
  empty,
  compact = false,
  className,
}: TableProps<Row>) {
  const align = (c: TableColumn<Row>) => ALIGN[c.align ?? (c.numeric ? 'end' : 'start')];
  return (
    <div
      className={cx('overflow-x-auto rounded-jc-info bg-jc-surface ring-1 ring-inset ring-jc-card-border', className)}
    >
      <table className={cx('w-full border-collapse text-sm text-jc-ink', compact && 'min-w-192 table-fixed')}>
        <caption className={showCaption ? 'px-4 pt-3 text-left text-sm font-semibold text-jc-ink' : 'sr-only'}>
          {caption}
        </caption>
        <thead>
          <tr className="border-b border-jc-border">
            {columns.map((c) => {
              const active = sort?.key === c.key;
              const ariaSort = active
                ? sort!.direction === 'asc'
                  ? 'ascending'
                  : 'descending'
                : c.sortable
                  ? 'none'
                  : undefined;
              return (
                <th
                  key={c.key}
                  scope="col"
                  aria-sort={ariaSort}
                  className={cx(
                    'type-eyebrow text-jc-ink-secondary',
                    compact ? 'px-2 py-2 align-bottom' : 'px-4 py-2.5 whitespace-nowrap',
                    align(c),
                    c.className,
                  )}
                >
                  {c.sortable && onSortChange ? (
                    <button
                      type="button"
                      onClick={() =>
                        onSortChange({ key: c.key, direction: active && sort!.direction === 'desc' ? 'asc' : 'desc' })
                      }
                      className={cx(
                        'inline-flex items-center gap-1 rounded-jc-info uppercase hover:text-jc-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus',
                        active && 'text-jc-ink',
                      )}
                    >
                      {c.label}
                      <Icon
                        icon={active && sort!.direction === 'asc' ? CaretUp : CaretDown}
                        size="sm"
                        className={active ? undefined : 'opacity-40'}
                      />
                    </button>
                  ) : (
                    c.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-jc-border">
          {rows.length === 0 && empty ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-8 text-center text-jc-ink-secondary">
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={rowKey(row)} className="transition-colors duration-(--jc-duration-fast) hover:bg-jc-elevated">
                {columns.map((c, i) => {
                  const Cell = i === 0 ? 'th' : 'td';
                  return (
                    <Cell
                      key={c.key}
                      scope={i === 0 ? 'row' : undefined}
                      className={cx(
                        compact ? 'px-2 py-2.5 align-middle' : 'px-4 py-3 align-middle',
                        i === 0 && 'font-semibold',
                        c.numeric && 'font-display font-bold tabular-nums whitespace-nowrap',
                        align(c),
                        c.className,
                      )}
                    >
                      {c.render(row)}
                    </Cell>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
